import {DEMO_CALC_ID, isDemoPage} from '../../../utils/hooks'

import {
  DemoExtraService,
  demoBankCommissionsCatalog,
  demoExtraServicesCatalog,
  getBody,
  getDemoExciseOptions,
  getDemoExtraServicesSearchResponse,
  getDemoIncotermsResponse,
  getDemoPlacesCalculation,
  getDemoSuggestAdditionalFields,
  getDictionaryResponse,
  getMethod,
  getOrderSummaryResponse,
  getParams,
  getProductsByInvoiceId,
  getScenarioDetails,
  getSelectedDemoExtraServices,
  getUrl,
  normalizeDemoHsCode,
  scenarioDetails,
  setDemoScenarioDetails,
  updateDemoExtraServices,
} from './scenario'

export const getDemoApiResponse = (args: any, state?: any) => {
  if (!isDemoPage()) {
    return undefined
  }

  const url = getUrl(args)
  const method = getMethod(args)
  const body = getBody(args)
  const params = getParams(args)
  const order = getScenarioDetails(state)?.order

  const bankCommissionsUpdateMatch = url?.match(
    /^calc-api\/v4\.0\/order\/invoice\/(\d+)\/banking_commissions\/$/,
  )

  if (bankCommissionsUpdateMatch && method === 'PUT') {
    if (!order) {
      return undefined
    }

    const invoiceId = Number(bankCommissionsUpdateMatch[1])

    let paymentValues = []

    if (Array.isArray(body)) {
      paymentValues = body
    }

    const backendInvoice = demoBankCommissionsCatalog?.invoices.find(invoice => invoice.id === invoiceId)
    const payments = paymentValues.map((payment, index) => {
      const backendPayment = backendInvoice?.banking_commissions[index]

      return {
        ...backendPayment,
        ...payment,
        id: payment.id ?? backendPayment?.id ?? index + 1,
      }
    })

    const updatedBankCommissions = {
      ...order.bankCommissions,
      invoices: order.bankCommissions.invoices.map(invoice => {
        if (invoice.id !== invoiceId) {
          return invoice
        }

        return {
          ...invoice,
          banking_commissions: payments,
        }
      }),
    }

    if (scenarioDetails) {
      setDemoScenarioDetails({
        ...scenarioDetails,
        order: {
          ...order,
          bankCommissions: updatedBankCommissions,
        },
      })
    }

    return payments
  }

  const suggestAdditionalFieldsMatch = url?.match(
    new RegExp(`^calc-api/v4\\.0/invoice/(\\d+)/product/suggest-additional-fields/$`),
  )

  if (suggestAdditionalFieldsMatch && method === 'POST') {
    return getDemoSuggestAdditionalFields(body?.code)
  }

  if (url === '/dictionary/v4.0/informers/' || url === 'dictionary/v4.0/informers/') {
    return []
  }

  if (url === 'dictionary/v4.0/currencies/') {
    return [
      { code: 'USD', name: 'US Dollar' },
      { code: 'RUB', name: 'Russian Ruble' },
    ]
  }

  if (url === 'dictionary/country/' || url?.startsWith('dictionary/country/?')) {
    const scenarioCountries = [
      order?.route.location_from.country,
      order?.route.location_to.country,
    ].filter((country): country is TestDriveCountry => Boolean(country))
    const countries = Array.from(new Map(
      scenarioCountries.map(country => [ country.code, country ]),
    ).values())
    const search = params?.search?.toLocaleLowerCase()
    const filteredCountries = search
      ? countries.filter(country => [
        country.code,
        country.name,
        country.name_eng,
        country.iso2,
        country.alta_code,
      ].some(value => value?.toLocaleLowerCase().includes(search)))
      : countries

    return getDictionaryResponse(filteredCountries)
  }

  if (url === 'dictionary/incoterm/') {
    return getDemoIncotermsResponse(order)
  }

  if (url === 'dictionary/v2.0/containers/') {
    return [
      { code: '20dc', label: '20 DC', name: '20 DC', sizes: '' },
      { code: '20dh', label: '20 DH', name: '20 DH', sizes: '' },
      { code: '40dc', label: '40 DC', name: '40 DC', sizes: '' },
      { code: '40hc', label: '40 HC', name: '40 HC', sizes: '' },
    ]
  }

  if (url === 'dictionary/v4.0/delivery_time/' || url === 'dictionary/v4.0/palletes/') {
    return []
  }

  if (url === 'dictionary/v4.0/tnved-hierarchy/') {
    return {
      results: [],
    }
  }

  if (url?.match(/^\/?certification\/v4\.0\/(declaration_types|document_durations)\/?(?:\?.*)?$/)) {
    return []
  }

  if (url?.match(/^\/?certification\/v4\.0\/documents\/?.*$/)) {
    return []
  }

  if (url === 'dictionary/v4.0/search/locations/' && order) {
    const locations = [
      {
        ...order.route.location_from,
        lat: 29.8683,
        lng: 121.544,
      },
      {
        ...order.route.location_to,
        lat: 55.7558,
        lng: 37.6173,
      },
    ]

    const query = params?.q?.toLocaleLowerCase()

    if (params?.id) {
      return locations.filter(location => location.id === params.id)
    }

    return query
      ? locations.filter(location => location.name.toLocaleLowerCase().includes(query))
      : locations
  }

  const locationByIdMatch = url?.match(/^dictionary\/v4\.0\/locations\/(.+)\/$/)

  if (locationByIdMatch && order) {
    return [
      { ...order.route.location_from, lat: 29.8683, lng: 121.544 },
      { ...order.route.location_to, lat: 55.7558, lng: 37.6173 },
    ]
      .find(location => location.id === locationByIdMatch[1]) || order.route.location_to
  }

  if (!order) {
    return undefined
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/`) {
    return method === 'POST' ? {
      ...order,
      ...body,
    } : order
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/completed/`) {
    const summaryCompleted = {
      ...order.summaryCompleted,
      ...body,
    }

    if (scenarioDetails) {
      setDemoScenarioDetails({
        ...scenarioDetails,
        order: {
          ...order,
          summaryCompleted,
        },
      })
    }

    return summaryCompleted
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/invoice/`) {
    return {
      id: order.invoices[0].id,
      ...body,
      products: body?.products || [],
    }
  }

  const orderInvoiceMatch = url?.match(new RegExp(`^calc-api/v4\\.0/order/${DEMO_CALC_ID}/invoice/(\\d+)/$`))

  if (orderInvoiceMatch) {
    const invoice = order.invoices.find(item => item.id === Number(orderInvoiceMatch[1]))

    return method === 'DELETE' ? invoice : {
      ...invoice,
      ...body,
    }
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/summary/`) {
    return method === 'POST' ? body : getOrderSummaryResponse(order)
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/invoices/`) {
    return order.invoices
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/packaging/`) {
    return method === 'POST' ? {
      ...order.summaryOrder,
      ...body,
    } : order.summaryOrder
  }

  if (url?.startsWith(`calc-api/v4.0/order/${DEMO_CALC_ID}/delivery/routes/`)) {
    return {
      count: 1,
      meta: {
        max_total_price: order.route.total_price,
        min_total_price: order.route.total_price,
      },
      results: [ order.route ],
    }
  }

  if (url === `calc-api/v4.0/calculate/price/`) {
    return {
      ...body,
      price_total: Number(body?.unit_price || 0) * Number(body?.pieces || 0),
    }
  }

  if (url === `calc-api/v4.0/calculate/places/`) {
    return getDemoPlacesCalculation(body)
  }

  const productsByInvoiceMatch = url?.match(/^calc-api\/v4\.0\/invoice\/(\d+)\/products\/$/)

  if (productsByInvoiceMatch) {
    return getProductsByInvoiceId(order, Number(productsByInvoiceMatch[1]))
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/declarations/`) {
    return order.declarations
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/customs/`) {
    return order.summaryCustoms
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/delivery/`) {
    return [ 'POST', 'PUT' ].includes(method) ? {
      ...order.route,
      ...body,
    } : order.route
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/summary-invoices/`) {
    return order.summaryInvoices
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/invoices/banking_commissions/`) {
    return order.bankCommissions
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/certification/codes/`) {
    return order.cert_codes
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/additional-services/`) {
    return order.additionalServices || []
  }

  if (url?.startsWith(`calc-api/v4.0/order/${DEMO_CALC_ID}/extra-services/search/`)) {
    return getDemoExtraServicesSearchResponse()
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/extra-services/`) {
    if (method === 'POST') {
      const selectedExtraServices = getSelectedDemoExtraServices(body?.service_id || [])

      updateDemoExtraServices(selectedExtraServices)

      return selectedExtraServices
    }

    if (method === 'DELETE') {
      const removedServiceIds = body?.service_id || []
      const selectedExtraServices = (order.userExtraServices || []).reduce<DemoExtraService[]>(
        (companies, company) => {
          if (body?.company_id === company.id) {
            return companies
          }

          const services = company.services
            .filter(service => !removedServiceIds.includes(service.id))
            .map(service => ({
              ...service,
              price: Number(service.price || 0),
              currency: company.currency || 'USD',
            }))

          if (services.length) {
            companies.push({
              ...company,
              cost_total: services.reduce((total, service) => total + service.price, 0),
              services,
            })
          }

          return companies
        },
        [],
      )

      updateDemoExtraServices(selectedExtraServices)

      return selectedExtraServices
    }

    return order.userExtraServices || []
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/broker/tariff/`) {
    return null
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/extra-services/companies/`) {
    return demoExtraServicesCatalog
  }

  if (url === `calc-api/v4.0/order/${DEMO_CALC_ID}/broker/tariff/companies/`) {
    return []
  }

  const tnvedMatch = url?.match(/^dictionary\/alta\/tnved\/(.+)\/$/)

  if (tnvedMatch) {
    const code = normalizeDemoHsCode(tnvedMatch[1])

    return {
      GoodInfo: {
        Code: code,
        Name: '',
        Exciselist: {
          Excise: getDemoExciseOptions(code),
        },
      },
    }
  }

  return undefined
}
